import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { compressMediaFiles } from '../utils/imageCompression';
import { normalizeVideoUrlsInText } from '../utils/videoProviders';
import { ImageEditorModal } from './LazyModals';

const MAX_CHARS = 280;

const QuoteBazzModal = ({ post, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();

  const [content, setContent] = useState('');
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);

  const charCount = content.length;
  const charsRemaining = MAX_CHARS - charCount;
  const progressPercent = Math.min((charCount / MAX_CHARS) * 100, 100);

  const ringRadius = 10;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const strokeDashoffset = ringCircumference - (progressPercent / 100) * ringCircumference;
  const ringColor =
    charsRemaining < 0
      ? '#ef4444'
      : charsRemaining <= 20
      ? '#f59e0b'
      : '#0284c7';

  const mediaRef = useRef(media);
  mediaRef.current = media;

  // Revoke any pending blob preview URLs on unmount
  useEffect(() => {
    return () => {
      mediaRef.current.forEach((item) => {
        if (item?.previewUrl && item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  const canSubmit = (content.trim().length > 0 || media.length > 0) && charCount <= MAX_CHARS;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (media.length + files.length > 4) {
      toast.error('Maximum 4 media attachments per post');
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'video/mp4',
        'video/webm',
        'video/quicktime',
      ];

      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Only images and videos are supported.`);
        continue;
      }

      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 50MB file size limit.`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const newMediaItems = validFiles.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      type: file.type.startsWith('video') ? 'video' : 'image',
      name: file.name,
      size: file.size,
    }));

    setMedia((prev) => [...prev, ...newMediaItems]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeMedia = (idx) => {
    setMedia((prev) => {
      const item = prev[idx];
      if (item?.previewUrl && item.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit || loading || uploading) return;
    setLoading(true);

    try {
      let finalMedia = [];
      const itemsToUpload = media.filter((m) => m.file);

      if (itemsToUpload.length > 0) {
        setUploading(true);
        setUploadProgress(10);

        const rawFiles = itemsToUpload.map((m) => m.file);
        const compressed = await compressMediaFiles(rawFiles, { type: 'post' });
        const formData = new FormData();
        formData.append('subfolder', 'posts');
        compressed.forEach((f) => formData.append('files', f));

        const res = await axios.post('/api/upload/multiple', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          },
        });

        const uploadedMedia = res.data.media || [];
        let uploadIdx = 0;
        for (const item of media) {
          if (!item.file) {
            finalMedia.push({ url: item.url, type: item.type || 'image' });
          } else if (uploadedMedia[uploadIdx]) {
            finalMedia.push(uploadedMedia[uploadIdx]);
            uploadIdx++;
          }
        }
      } else {
        finalMedia = media.map((m) => ({ url: m.url, type: m.type || 'image' }));
      }

      const normalized = normalizeVideoUrlsInText(content.trim() || '');
      await axios.post('/api/posts', {
        content: normalized,
        media: finalMedia,
        quotePostId: post.id,
      });

      // Revoke any blob preview URLs
      media.forEach((item) => {
        if (item.previewUrl && item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });

      toast.success('Quote Bazz posted!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post');
    } finally {
      setLoading(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Quoted post mini-card data
  const qp = post;
  const qHandle = (qp.author?.handle || qp.author?.username || '').replace(/^@+/, '');
  const qUsername = qp.author?.username || qHandle || 'User';
  const qPic = qp.author?.profilePicture;
  const qExcerpt = qp.content ? qp.content.slice(0, 200) + (qp.content.length > 200 ? '…' : '') : '';
  const qMedia = (qp.media || []).find((m) => m.type === 'image');

  const userProfilePic = user?.profilePicture || user?.profile_picture;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-xs select-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div className="bg-white dark:bg-zinc-950 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-xl max-h-[88vh] flex flex-col border border-zinc-200 dark:border-zinc-800 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-0 overflow-hidden">
        {/* Mobile drag grab handle */}
        <div className="sm:hidden pt-2.5 pb-1 flex justify-center">
          <div className="w-10 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800/80">
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Close"
          >
            <i className="fas fa-times text-base" />
          </button>
          <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[16px]">{t('quote.quoteTitle', 'Quote Bazz')}</span>
          {/* Post button (also duplicated at bottom for convenience) */}
          <div className="w-9" />
        </div>

        {/* Composer Area */}
        <div className="px-4 pt-4">
          <div className="flex gap-3">
            {/* Current user avatar */}
            <div className="flex-shrink-0">
              {userProfilePic ? (
                <img
                  src={getMediaUrl(userProfilePic)}
                  alt={user?.username || 'You'}
                  className="w-10 h-10 rounded-full object-cover border border-zinc-200 dark:border-zinc-800"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-bold text-zinc-600 dark:text-zinc-300 text-sm">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('quote.addThoughts', 'Add your thoughts…')}
                className="w-full border-none resize-none focus:outline-none text-[17px] text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-600 font-normal bg-transparent leading-relaxed"
                rows={3}
                autoFocus
              />
            </div>
          </div>

          {/* Media preview */}
          {media.length > 0 && (
            <div
              className={`mt-2 mb-3 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 ${
                media.length === 1 ? '' : 'grid grid-cols-2 gap-1.5 p-1'
              }`}
            >
              {media.map((item, idx) => {
                const mediaSrc = item.previewUrl || (item.url ? getMediaUrl(item.url) : '');
                return (
                  <div key={item.id || idx} className="relative group overflow-hidden rounded-xl">
                    {item.type === 'video' ? (
                      <video
                        src={mediaSrc}
                        className={`w-full ${media.length === 1 ? 'max-h-64 object-contain bg-black' : 'h-36 object-cover'}`}
                        controls
                      />
                    ) : (
                      <img
                        src={mediaSrc}
                        alt={`Attachment ${idx + 1}`}
                        className={`w-full ${media.length === 1 ? 'max-h-64 object-contain bg-zinc-100 dark:bg-zinc-900' : 'h-36 object-cover'}`}
                      />
                    )}
                    {item.type === 'image' && (
                      <button
                        type="button"
                        onClick={() => setEditingImageIndex(idx)}
                        className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center gap-1.5 text-xs font-semibold backdrop-blur-md transition-all shadow-md group-hover:scale-105 z-20"
                        title={t('imageEditor.title', 'Edit Image')}
                      >
                        <i className="fas fa-magic text-xs text-sky-400" />
                        <span>{t('common.edit', 'Edit')}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeMedia(idx)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-all shadow-md z-20"
                    >
                      <i className="fas fa-times text-xs" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload progress */}
          {uploading && (
            <div className="w-full bg-zinc-100 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden mb-2">
              <div
                className="bg-sky-500 h-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* Quoted Post Mini-Card */}
          <div className="mt-1 mb-4 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-3">
            <div className="flex items-center gap-2 mb-1.5">
              {qPic ? (
                <img
                  src={getMediaUrl(qPic)}
                  alt={qUsername}
                  className="w-5 h-5 rounded-full object-cover border border-zinc-200 dark:border-zinc-700 shrink-0"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-zinc-600 dark:text-zinc-300 shrink-0">
                  {qUsername.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[13.5px] truncate">
                <bdi>{qUsername}</bdi>
              </span>
              {qHandle && (
                <span className="text-zinc-500 dark:text-zinc-400 text-xs truncate">
                  <bdi dir="ltr">@{qHandle}</bdi>
                </span>
              )}
            </div>
            {qExcerpt && (
              <p className="text-zinc-700 dark:text-zinc-300 text-[14px] leading-snug break-words whitespace-pre-wrap mb-1.5">
                {qExcerpt}
              </p>
            )}
            {qMedia && (
              <img
                src={getMediaUrl(qMedia.url)}
                alt="Quoted media"
                className="w-full max-h-36 object-cover rounded-xl border border-zinc-200 dark:border-zinc-800"
              />
            )}
          </div>
        </div>

        {/* Toolbar & Submit */}
        <div className="flex items-center justify-between px-4 pb-4 border-t border-zinc-100 dark:border-zinc-800/80 pt-3">
          <div className="flex items-center gap-1 text-sky-500 dark:text-sky-400">
            {/* Photo upload */}
            <label
              className="w-8 h-8 rounded-full hover:bg-sky-50 dark:hover:bg-sky-950/40 flex items-center justify-center cursor-pointer transition-colors"
              title="Photos or videos"
            >
              <i className="fas fa-image text-base" />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading || media.length >= 4}
              />
            </label>
          </div>

          <div className="flex items-center gap-3">
            {charCount > 0 && (
              <div className="flex items-center gap-1.5">
                <svg className="w-7 h-7 -rotate-90">
                  <circle
                    cx="14"
                    cy="14"
                    r={ringRadius}
                    stroke="#e5e7eb"
                    className="dark:stroke-zinc-800"
                    strokeWidth="2.5"
                    fill="none"
                  />
                  <circle
                    cx="14"
                    cy="14"
                    r={ringRadius}
                    stroke={ringColor}
                    strokeWidth="2.5"
                    strokeDasharray={ringCircumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    fill="none"
                    className="transition-all duration-150"
                  />
                </svg>
                {charsRemaining <= 20 && (
                  <span
                    className={`text-xs font-bold ${
                      charsRemaining < 0 ? 'text-rose-500' : 'text-amber-500'
                    }`}
                  >
                    {charsRemaining}
                  </span>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || loading || uploading}
              className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-5 py-2 rounded-full font-bold text-sm hover:bg-zinc-800 dark:hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
            >
              {loading ? t('quote.posting', 'Posting…') : t('common.post', 'Post')}
            </button>
          </div>
        </div>
      </div>

      {/* Image Filter & Editor Modal */}
      {editingImageIndex !== null && media[editingImageIndex] && (
        <ImageEditorModal
          isOpen={editingImageIndex !== null}
          imageUrl={media[editingImageIndex].previewUrl || media[editingImageIndex].url}
          onClose={() => setEditingImageIndex(null)}
          onSave={(newMedia) => {
            setMedia((prev) => {
              const copy = [...prev];
              const prevItem = copy[editingImageIndex];
              if (
                prevItem?.previewUrl &&
                prevItem.previewUrl.startsWith('blob:') &&
                prevItem.previewUrl !== newMedia.previewUrl
              ) {
                URL.revokeObjectURL(prevItem.previewUrl);
              }
              copy[editingImageIndex] = {
                ...prevItem,
                id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                file: newMedia.file,
                previewUrl: newMedia.previewUrl,
                url: newMedia.previewUrl,
                type: 'image',
                name: newMedia.file?.name || prevItem?.name || 'edited_image.jpg',
                size: newMedia.size || newMedia.file?.size,
              };
              return copy;
            });
            setEditingImageIndex(null);
          }}
          subfolder="posts"
          uploadOnSave={false}
        />
      )}
    </div>,
    document.body
  );
};

export default QuoteBazzModal;
